var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import { CARD_MIN_ZOOM, filtersDefault, getPropertyCard, getUnclaimedParcelCard, getRectangleMapItems, } from '../../../../../common/network/property';
import { isPropertyCard, } from '../../../../../types';
import { createDisplayConfig, regridParcelLayerURL } from '../constants';
import { filtersGeoRectangle, useViewportLoad } from './useViewportLoad';
// The continental US, for a map opening without a chosen viewport.
export const NATIONAL_CENTER = [-86.16, 39.77];
export const NATIONAL_ZOOM = 4;
export const parcelBordersLayer = {
    url: regridParcelLayerURL,
    opacity: 0.6,
    minScale: 25000,
    maxScale: 0,
    visible: true,
    displayName: 'Parcel Borders',
    showToggle: true,
};
// Every property map draws its marker from the parcel kind and carries the
// aggregate count through for the cluster label, plus whether the marker stands for a
// contributed property or a parcel still waiting for one.
export const propertyMapItemProps = {
    mapItem: (x) => x,
    mapItemToMarker: (x) => x.kind && Object.assign(Object.assign({}, x.kind), { id: x.kind.name }),
    mapItemToAttributes: (x) => ({
        count: x.count,
        claimed: x.claimed ? 1 : 0,
    }),
};
export const districtAreas = (districts) => { var _a; return (_a = districts === null || districts === void 0 ? void 0 : districts.flatMap(d => d.area ? [d.area] : [])) !== null && _a !== void 0 ? _a : []; };
// Mirrors the tier `getRectangleMapItems` picks.
const cardsLoaded = (mapView) => Math.round(mapView.zoom) >= CARD_MIN_ZOOM;
const fetchPropertyCard = (id, claimed) => __awaiter(void 0, void 0, void 0, function* () {
    const fetched = yield (claimed ? getPropertyCard(id) : getUnclaimedParcelCard(id));
    if (!fetched)
        throw new Error(`${claimed ? 'Property' : 'Parcel'} ${id} was not found`);
    return fetched;
});
// The popup renders from a promise, so one id must yield the same promise across renders.
// An already-loaded card resolves outright; anything else falls back to one fetch.
export const usePropertyCardCache = () => {
    const cacheRef = React.useRef(new Map());
    const propertyPromise = React.useCallback((x) => {
        const cached = cacheRef.current.get(x.id);
        const promise = cached !== null && cached !== void 0 ? cached : (isPropertyCard(x)
            ? Promise.resolve(x)
            : fetchPropertyCard(x.id, x.claimed).catch((e) => {
                cacheRef.current.delete(x.id);
                throw e;
            }));
        cacheRef.current.set(x.id, promise);
        return promise;
    }, []);
    // Callers holding a row that already has everything the card renders can skip the fetch.
    const seedProperty = React.useCallback((x) => {
        if (!cacheRef.current.has(x.id))
            cacheRef.current.set(x.id, Promise.resolve(x));
    }, []);
    return { propertyPromise, seedProperty };
};
// Owns the state and viewport loading every property map needs: the view handle,
// the display config, clustering, and the points currently in frame.
export const usePropertyMap = ({ center, zoom, filters }) => {
    const [mapView, setMapView] = React.useState(null);
    const [mapReady, setMapReady] = React.useState(false);
    const [longitude, latitude] = center;
    const onMapReady = React.useCallback((mv) => {
        setMapView(mv);
        setMapReady(true);
    }, []);
    // `config.center` drives a `goTo` effect inside the map, so it has to hold a
    // stable identity across renders even when the caller rebuilds the array.
    const config = React.useMemo(() => createDisplayConfig({
        center: [longitude, latitude],
        zoom,
        showBasemapSelector: true,
    }), [longitude, latitude, zoom]);
    const clustering = React.useMemo(() => ({
        clusterRadius: '76px',
        clusterMinSize: '28px',
        clusterMaxSize: '54px',
        maxScale: 5000,
    }), []);
    const { items, total, isLoading } = useViewportLoad({
        mapView,
        mapReady,
        fetchFn: getRectangleMapItems,
        filtersDefault: Object.assign(Object.assign({}, filtersDefault()), { pageSize: 0, latmin: 0, latmax: 0, lngmin: 0, lngmax: 0, zoom: 0 }),
        filtersGeo: filtersGeoRectangle,
        filters,
        loadKey: cardsLoaded,
    });
    return {
        mapView,
        mapReady,
        onMapReady,
        config,
        clustering,
        mapPoints: items,
        mapPointsTotal: total,
        isLoading,
    };
};
